import React, { useCallback, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useDropzone } from 'react-dropzone';
import { toast } from 'react-toastify';
import { Box, Button, IconButton, useTheme } from '@material-ui/core';
import { ArrowBack, InsertDriveFile } from '@material-ui/icons';
import { Card, FullScreenDialog, Toolbar, Typography } from '../../../../ui-kit';
import { UploadDeliveryListDropzone } from './style';

interface UploadDeliveryListFormProps {
    dialogName: string;
    isOpened: boolean;
    onUpload: (files: File[]) => void;
    onClose: () => void;
}

const UploadDeliveryListDialog = ({ dialogName, isOpened, onClose, onUpload }: UploadDeliveryListFormProps) => {
    const { t } = useTranslation();
    const theme = useTheme();
    const [files, setFiles] = useState<File[]>([]);

    const onDrop = useCallback((acceptedFiles, rejectedFiles) => {
        setFiles(prevFiles => [...prevFiles, ...acceptedFiles]);

        rejectedFiles.forEach(({ file, errors }: { file: File; errors: Array<{ code: string }> }) => {
            const { name } = file;
            let message = '';

            switch (errors[0]?.code) {
                case 'file-invalid-type':
                    message = t('file type is not supported');
                    break;

                case 'file-too-large':
                    message = t('file is too large');
                    break;

                default:
                    break;
            }

            const errorPrefix = t('Could not upload');
            const errorMessage = `${errorPrefix} ${name}: ${message}`;

            toast(errorMessage, { type: 'error' });
        });
    }, [t]);

    const { getRootProps, getInputProps, isDragActive } = useDropzone({
        onDrop,
        maxSize: 13 * 1024 * 1024, // 13 MB
        accept: 'application/pdf,image/jpeg,image/png,image/tiff',
    });

    const handleUpload = () => {
        onUpload(files);
        onClose();
    };

    const dropzoneProps = {
        ...getRootProps(),
        backgroundColor: theme.palette.success.light,
        borderColor: theme.palette.success.main,
        isBorderDashed: !isDragActive,
    };

    const dragAndDropTitle = t('Drag&Drop a file or an image or browse');
    const supportedFormatsText = t('Supported file formats are PDF, PNG, JPEG, and TIFF');
    const maxSizeText = t('Size of a single document may not exceed 13 MB');
    const onePagePerFileText = t('One page may contain only one document');

    const emptyDropzoneContent = (
        <Box height='100%' display='flex' flexDirection='column' justifyContent='center' alignItems='center'>
            <Typography variant='h6' component='h1'>{dragAndDropTitle}</Typography>
            <Box mb={2} />
            <Typography variant='body1' component='span'>{supportedFormatsText}</Typography>
            <Typography variant='body1' component='span'>{maxSizeText}</Typography>
            <Typography variant='body1' component='span'>{onePagePerFileText}</Typography>
        </Box>
    );

    const droppedFilesList = files.map(({ name }) => (
        <Box key={name} pt={2} px={2} width='100%'>
            <Card fullHeight>
                <Box p={2} display='flex' alignItems='center'>
                    <InsertDriveFile color='primary' />
                    <Box ml={2} />
                    <Typography variant='body1' component='span'>{name}</Typography>
                </Box>
            </Card>
        </Box>
    ));

    return (
        <FullScreenDialog isOpened={isOpened}>
            <Card style={{ height: '100%' }}>
                <Box px={3} py={2}>
                    <IconButton edge="start" color="inherit" onClick={onClose} aria-label="close">
                        <ArrowBack />
                    </IconButton>
                    <Box mb={2} />
                    <Typography variant='h6' component='h1'>{dialogName}</Typography>
                    <UploadDeliveryListDropzone {...dropzoneProps}>
                        <input {...getInputProps()} />
                        {files.length > 0 ? droppedFilesList : emptyDropzoneContent}
                    </UploadDeliveryListDropzone>
                </Box>
                <Toolbar isBottom disableDrawerOffset>
                    <Box display='flex' justifyContent='center' flexGrow={1}>
                        <Button
                            variant="contained"
                            color="primary"
                            onClick={handleUpload}
                            disabled={files.length === 0}
                        >
                            {t('Upload')}
                        </Button>
                    </Box>
                </Toolbar>
            </Card>
        </FullScreenDialog>
    );
};

export default UploadDeliveryListDialog;
